import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  Newspaper,
  Users,
  UserRound,
  FileText,
  FileOutput,
  LogOut,
  Lock,
  Menu,
  X,
} from "lucide-react";
import { ChangePasswordDialog } from "../../components/changePasswordDialog";
import LogoLarge from "../../data/logo.png";
import LogoSmall from "../../data/logo_small.png"

const Sidebar = () => {
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const validateAdmin = async () => {
      try {
        const token = document.cookie
          .split("; ")
          .find(row => row.startsWith("token"))
          ?.split("=")[1];

        if (!token) return;

        const response = await fetch(
          "https://preview.zushm.cz/api/auth/validateadmin",
          { headers: { Authorization: token } }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.message === "User is an admin") setIsAdmin(true);
        }
      } catch (error) {
        console.error("Error validating admin:", error);
      }
    };

    validateAdmin();
  }, []);

  const baseMenuItems = [
    { to: "/dashboard/novinky", label: "Novinky", icon: <Newspaper size={24} /> },
  ];

  const adminMenuItems = [
    { to: "/dashboard/uzivatele", label: "Uživatelé", icon: <Users size={24} /> },
    { to: "/dashboard/zamestnanci", label: "Zaměstnanci", icon: <UserRound size={24} /> },
    { to: "/dashboard/prijimacirizeni", label: "Přijímací řízení", icon: <FileText size={24} /> },
  ];

  const commonMenuItems = [
    {
      to: "/dashboard/prijimacirizeniexport",
      label: "Export přijímacího řízení",
      icon: <FileOutput size={24} />,
    },
  ];

  const menuItems = [
    ...baseMenuItems,
    ...(isAdmin ? adminMenuItems : []),
    ...commonMenuItems,
  ];

  const handleLogout = () => {
    document.cookie = "token=logout; path=/; Secure";
    window.location.href = "/adminlogin";
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      {/* ================= MOBILE TOP BAR ================= */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-gray-100 border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/dashboard">
            <img src={LogoSmall} alt="Logo" className="w-9" />
          </Link>

          <button
            onClick={() => setIsMobileMenuOpen(v => !v)}
            className="p-2 rounded-lg border bg-white"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* MOBILE DROPDOWN MENU */}
        {isMobileMenuOpen && (
          <div className="mt-3 border-t pt-3 space-y-2">
            {menuItems.map((item, i) => (
              <Link
                key={i}
                to={item.to}
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white"
              >
                {item.icon}
                <span className="text-lg">{item.label}</span>
              </Link>
            ))}

            <Button
              variant="outline"
              className="w-full justify-start py-3 text-lg"
              onClick={() => {
                setIsChangePasswordOpen(true);
                closeMobileMenu();
              }}
            >
              <Lock className="mr-2 h-5 w-5" />
              Změnit heslo
            </Button>

            <Button
              variant="destructive"
              className="w-full justify-start py-3 text-lg"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-5 w-5" />
              Odhlásit se
            </Button>
          </div>
        )}
      </div>

      {/* spacer so page content is not hidden behind fixed mobile bar */}
      <div className="md:hidden h-20" />

      {/* ================= DESKTOP SIDEBAR (ORIGINAL) ================= */}
      <nav className="hidden md:flex flex-col h-screen bg-gray-100 w-64 pl-2 pr-2">
        <div className="flex justify-center items-center mb-6">
          <Link to="/dashboard">
            <img
              src={LogoLarge}
              alt="Logo"
              className="w-40"
              style={{ marginRight: "20px", marginTop: "5px" }}
            />
          </Link>
        </div>

        <ul className="space-y-2 flex-1">
          {menuItems.map((item, index) => (
            <li key={index}>
              <Link
                to={item.to}
                className="flex items-center space-x-2 text-gray-700 hover:text-black ml-1"
              >
                {item.icon}
                <span className="text-lg">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-6 space-y-2 pb-2">
          <Button
            variant="outline"
            className="w-full justify-start py-3 text-lg ml-2"
            onClick={() => setIsChangePasswordOpen(true)}
          >
            <Lock className="mr-2 h-5 w-5" />
            Změnit heslo
          </Button>

          <Button
            variant="destructive"
            className="w-full justify-start py-3 text-lg ml-2"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-5 w-5" />
            Odhlásit se
          </Button>
        </div>
      </nav>

      <ChangePasswordDialog
        open={isChangePasswordOpen}
        onOpenChange={setIsChangePasswordOpen}
      />
    </>
  );
};

export default Sidebar;
